import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  FilterFlyoutIconName,
  GenericIconName,
  IconSize,
  UnstyledButton,
  mediaDesktopAndUp,
  mediaLargeScreens,
  useKeyPress,
} from '@aboutyou/ui-components';
import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React from 'react';
import {
  ConditionalSearchSuggestionsHistory,
  MIN_CHARS_TO_SEARCH,
} from '../../SearchSuggestions/ConditionalSearchSuggestionsHistory';
import {HEADER_DEFAULT_PADDING, SEARCH_BAR_TABLET_WIDTH} from '../constants';
import {
  SearchBarWrapper,
  SearchBarWrapperTablet,
  StyledSearchIcon as SearchIcon,
  StyledInput as SearchInput,
  StyledCloseIcon,
  Wrapper,
} from './SearchBar.eager';
import {useSearchBar} from './hooks/useSearchBar';
import {
  SEARCH_BAR_ANIMATION_TIME_MS,
  useSearchSuggestions,
} from './hooks/useSearchSuggestions';

interface SearchBarProps {
  isSearchBarExpanded: boolean;
  expandSearchBar: VoidFunction;
  collapseSearchBar: VoidFunction;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
  isSearchInTabletMode: boolean;
  className?: string;
}

export const SearchSuggestions: React.FC<SearchBarProps> = ({
  isSearchInTabletMode,
  isSearchBarExpanded,
  expandSearchBar,
  collapseSearchBar,
  searchInputRef,
  searchWrapperRef,
  className,
}) => {
  const {t} = useTranslation();
  const {
    searchBarRef,
    isInputFocused,
    searchValue,
    onFocusSearchInput,
    handleInputOnChange,
    handleRemoveInputFocus,
    handleOnClickSearchIcon,
    collapseAndBlurInput,
    setSuggestionSelected,
    suggestionSelected,
  } = useSearchBar({
    isSearchInTabletMode,
    isSearchBarExpanded,
    collapseSearchBar,
    expandSearchBar,
    searchInputRef,
    searchWrapperRef,
  });

  const {
    clearSearchAndFocus,
    createSearchPath,
    handleInputOnChangeWithDebounce,
    handleInputOnChangeTrackingWithDebounce,
    onClickSuggestion,
    onSearchInputEnter,
    shouldShowCloseIcon,
    clearButtonOnKeyDownHandler,
    tabletOpenButtonRef,
  } = useSearchSuggestions({
    collapseAndBlurInput,
    handleInputOnChange,
    handleRemoveInputFocus,
    searchInputRef,
    isSearchBarExpanded,
    isSearchInTabletMode,
    searchValue,
    searchWrapperRef,
  });

  useKeyPress({
    key: 'Enter',
    onKeyDown: onSearchInputEnter,
    disabled:
      !isInputFocused ||
      (searchValue.length >= MIN_CHARS_TO_SEARCH && suggestionSelected),
  });

  const OnInputFocusAnimationCss = isSearchInTabletMode
    ? OnInputFocusAnimationTabletCss
    : OnInputFocusAnimationDesktopCss;

  return (
    <Wrapper
      ref={searchWrapperRef}
      data-testid="searchBar"
      isSearchBarExpanded={isSearchBarExpanded}
      className={className}
    >
      <StyledSearchBarWrapper
        className={cx(
          isSearchBarExpanded
            ? SearchWrapperExpandedCss
            : SearchWrapperClosedCss,
          isSearchInTabletMode
            ? SearchBarWrapperTablet
            : SearchBarWrapperDesktop,
          ((!isSearchInTabletMode && isInputFocused) ||
            (isSearchInTabletMode && isSearchBarExpanded)) &&
            OnInputFocusAnimationCss,
        )}
        ref={searchBarRef}
      >
        <SearchIconTransitionWrapper
          ref={tabletOpenButtonRef}
          $iconWidth={
            IconSize[isSearchInTabletMode && !isSearchBarExpanded ? 'S' : 'XS']
          }
          className={cx(
            !isSearchInTabletMode &&
              isInputFocused &&
              SearchIconTransitionWrapperHideTransition,
          )}
          aria-label={t('Open search bar')}
          onClick={handleOnClickSearchIcon}
          data-testid="searchIcon"
          tabIndex={isSearchInTabletMode ? 0 : -1}
        >
          <StyledSearchIcon
            asset={GenericIconName.Search}
            size={isSearchInTabletMode && !isSearchBarExpanded ? 'S' : 'XS'}
          />
        </SearchIconTransitionWrapper>
        <StyledInput
          data-testid="searchBarInput"
          placeholder={t('Search for items, brands and more...')}
          ref={searchInputRef}
          onChange={e => {
            handleInputOnChangeWithDebounce(e.target.value);
            handleInputOnChangeTrackingWithDebounce(e.target.value);
          }}
          onFocus={onFocusSearchInput}
          data-is-focused={isInputFocused}
          tabIndex={isSearchInTabletMode && !isSearchBarExpanded ? -1 : 0}
        />
        <CloseIconTransitionWrapper
          data-opened={shouldShowCloseIcon}
          aria-label={t('Clear search bar')}
          data-testid="searchBarCloseIcon"
          onClick={clearSearchAndFocus}
          tabIndex={shouldShowCloseIcon ? 0 : -1}
          onKeyDown={clearButtonOnKeyDownHandler}
        >
          <StyledCloseIcon
            asset={FilterFlyoutIconName.Delete}
            size="XS"
            fill={COLORS.Grey2}
          />
        </CloseIconTransitionWrapper>
      </StyledSearchBarWrapper>
      <ConditionalSearchSuggestionsHistory
        search={searchValue}
        handleRemoveInputFocus={handleRemoveInputFocus}
        isInputFocused={isInputFocused}
        searchBarRef={searchBarRef}
        isSearchBarExpanded={isSearchBarExpanded}
        createSearchPath={createSearchPath}
        onClickSuggestion={onClickSuggestion}
        onNavigateSuggestionList={setSuggestionSelected}
      />
    </Wrapper>
  );
};

const StyledSearchBarWrapper = styled(SearchBarWrapper)`
  &[data-is-focused='true'] {
    border-radius: 2px 2px 0 0;
  }
`;

const StyledInput = styled(SearchInput)`
  margin-left: 8px;
  font-size: clamp(${FONT_SIZES.px11}, 1vw, ${FONT_SIZES.px14});
`;

const SEARCH_ICON_MARGIN_LEFT = 8;

const StyledSearchIcon = styled(SearchIcon)`
  margin-left: ${SEARCH_ICON_MARGIN_LEFT}px;
  // the icon needs to be 18x18 pixels to be 1:1 with the design
  // https://www.figma.com/file/78aeShVkIg8jpHIdts5ufT/DT-4747---Search---Auto-Suggest?node-id=968%3A39892&mode=dev
  width: 18px;
  height: 18px;
`;

const SearchWrapperExpandedCss = css`
  &&& {
    top: 8px;
    height: calc(100% - 16px);
    width: calc(100% - ${HEADER_DEFAULT_PADDING.tablet * 2}px);
  }

  @media ${mediaDesktopAndUp} {
    width: calc(100% - ${HEADER_DEFAULT_PADDING.desktop * 2}px);
    right: 30px;
  }

  @media ${mediaLargeScreens} {
    width: calc(100% - ${HEADER_DEFAULT_PADDING.largeScreens * 2}px);
  }
`;

const SearchWrapperClosedCss = css`
  &&& {
    width: ${SEARCH_BAR_TABLET_WIDTH}px;
    overflow: hidden;
    border: none;
    height: 36px;
  }

  ${StyledSearchIcon} {
    position: relative;
    left: 50%;
    transform: translate(-${HEADER_DEFAULT_PADDING.tablet / 2}px);
  }

  ${StyledInput} {
    display: none;
  }
`;

const SearchBarWrapperDesktop = css`
  &&& {
    position: relative;
    top: unset;
    right: unset;
    width: 100%;
    border-color: ${COLORS.Grey4};
    background: ${COLORS.Grey4};
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: transform ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in,
      width ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in,
      box-shadow ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;
  }

  ${StyledSearchIcon} {
    position: relative;
    left: unset;
    transform: unset;
  }

  ${StyledInput} {
    display: block;
  }
`;

const OnInputFocusAnimationDesktopCss = css`
  &&& {
    box-shadow: -27px 0 16px -2px ${COLORS.White};
    transform: translateX(-15%);
    width: 115%;
    background-color: ${COLORS.White};
    border: 1px solid ${COLORS.Black};

    ${StyledInput} {
      background-color: ${COLORS.White};
    }

    ${StyledCloseIcon} {
      background-color: ${COLORS.White};
    }
  }
`;

const OnInputFocusAnimationTabletCss = css`
  &&& {
    box-shadow: -27px 0 16px -2px ${COLORS.White};
    background-color: ${COLORS.White};
    border: 1px solid ${COLORS.Black};

    ${StyledInput} {
      background-color: ${COLORS.White};
    }

    ${StyledCloseIcon} {
      background-color: ${COLORS.White};
    }
  }
`;

const SearchIconTransitionWrapper = styled(UnstyledButton)<{
  $iconWidth: number;
}>`
  width: ${({$iconWidth}) => $iconWidth + SEARCH_ICON_MARGIN_LEFT}px;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: width ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;
`;

const SearchIconTransitionWrapperHideTransition = css`
  overflow: hidden;
  width: 0;
`;

const CloseIconTransitionWrapper = styled(UnstyledButton)`
  margin-right: 5px;
  opacity: 0;
  transition: opacity ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;

  &[data-opened='true'] {
    overflow: hidden;
    opacity: 1;
  }
`;
